/**
 * <em-accordion> — animates a native <details> open/close.
 * Height eases between the summary height and the full height, and the panel fades in slightly after.
 */
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class EmAccordion extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    this.summary = this.details?.querySelector('summary');
    this.panel = this.details?.lastElementChild;
    if (!this.details || !this.summary) return;

    this.classList.toggle('is-open', this.details.open);
    this.onToggleClick = (event) => {
      event.preventDefault();
      this.details.open && this.classList.contains('is-open') ? this.close() : this.open();
    };
    this.summary.addEventListener('click', this.onToggleClick);

    this.onSelect = (event) => this.open(event.detail?.load);
    this.onDeselect = () => this.close();
    this.addEventListener('shopify:block:select', this.onSelect);
    this.addEventListener('shopify:block:deselect', this.onDeselect);
  }

  disconnectedCallback() {
    this.summary?.removeEventListener('click', this.onToggleClick);
    this.removeEventListener('shopify:block:select', this.onSelect);
    this.removeEventListener('shopify:block:deselect', this.onDeselect);
  }

  #stop() {
    this.heightAnimation?.cancel();
    this.panelAnimation?.cancel();
  }

  async open(instant = false) {
    this.#stop();
    const from = this.details.open ? this.details.offsetHeight : this.summary.offsetHeight;
    this.classList.add('is-open');
    this.details.open = true;
    if (instant || reducedMotion()) return;

    const to = this.details.offsetHeight;
    this.details.style.overflow = 'hidden';
    this.heightAnimation = this.details.animate({ height: [`${from}px`, `${to}px`] }, { duration: 250, easing: 'ease-in-out' });
    this.panelAnimation = this.panel?.animate(
      { opacity: [0, 1], transform: ['translateY(4px)', 'translateY(0)'] },
      { duration: 150, delay: 150, easing: 'ease-out', fill: 'backwards' }
    );
    try {
      await this.heightAnimation.finished;
    } catch (e) {
      /* cancelled */
    }
    this.details.style.overflow = '';
  }

  async close() {
    if (!this.details.open) return;
    this.#stop();
    this.classList.remove('is-open');
    if (reducedMotion()) {
      this.details.open = false;
      return;
    }

    const from = this.details.offsetHeight;
    const to = this.summary.offsetHeight;
    this.details.style.overflow = 'hidden';
    this.panelAnimation = this.panel?.animate({ opacity: [1, 0] }, { duration: 150, fill: 'forwards' });
    this.heightAnimation = this.details.animate({ height: [`${from}px`, `${to}px`] }, { duration: 250, easing: 'ease-in-out' });
    try {
      await this.heightAnimation.finished;
      if (!this.classList.contains('is-open')) this.details.open = false;
    } catch (e) {
      /* cancelled */
    }
    this.panelAnimation?.cancel();
    this.details.style.overflow = '';
  }
}

if (!customElements.get('em-accordion')) {
  customElements.define('em-accordion', EmAccordion);
}
